import "./section.scss";
import "./GamePage.scss";
import Button from "./components/Button";
import { useDispatch, useSelector } from "react-redux";
import ChartComponent from "./gaming_components/ChartComponent/ChartComponent";
/** @typedef {import('./types/gameData').GameData} GameData */
/** @typedef {import('./types/gameData').PlayerState} PlayerState */
import { 
    toggleGame,
    setRoomType,
    setRoomPlayers,
    setGameData,
    addPlayerToRoom, 
    updatePlayerWPM, 
    updatePlayerWord, 
    deletePlayerFromRoom, 
    toggleNotStart, 
    setCountDown, 
    toggleCountDown, 
    setRoomStartTime, 
    updateEstimatedTime, 
    endAllPlayerGames,
    updatePlayerPosition,
    resetGameState,
    resetGameStateForTransition,
    resetGameStatus,
    startCountDown,
    setNotStart,
    setCountDownState,
    toggleLoader,
    resetRaceStats,
    setIsLeavingRoom,
    setPracticeCountdownStartTime,
    clearPracticeCountdownStartTime,
    setPracticeGameStartTime,
    clearPracticeGameStartTime
} from "./redux/stores/GameStore";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import PlayerModel from "./gaming_components/PlayerModel/PlayerModel";
import GameInput from "./gaming_components/GameInput/GameInput";
import { loadGameAssets, parseQuote } from "./gaming_components/LoadGame/LoadGame";
import { socket } from "./socket";
import CountDown from "./gaming_components/Countdown/Countdown";
import GameTimer from "./gaming_components/GameTimer/GameTimer";
import useHotKey from "./hooks/useHotKey";
import Loader from "./gaming_components/Loader/Loader";
import store from "./redux/stores/GameStore";
export default function GamePage(){    
    const { 
        gameData,
        room, 
        multiplayer_id, 
        solo_id,
        countdownFlag,
        countdown,
        notStart,
        race_stats,
        isLeavingRoom,
        practiceCountdownStartTime,
        practiceGameStartTime,
        practiceGameInitialTime
        } = useSelector(state => state.game);
    const inputElement = useRef(null)
    const [baseCurrIdx, setBaseIdx] = useState(0);        
    const roomType = room.gameType;
    const player_id = roomType === "PRACTICE" ? solo_id : multiplayer_id;    
    const playerState = room.players && player_id ? room.players[player_id] : undefined;
    const currWordIdx = typeof playerState?.currentWord === "number" ? playerState.currentWord : 0;
    const gameEnd = playerState?.gameEnd === true;
    const handleGamePageHotKeys = (event) => {
        switch (event.key) {
            case 'Escape':
                if(document.activeElement === inputElement.current){
                    inputElement.current.blur();
                }
                else if(document.activeElement !== inputElement.current){
                    exitGameHandler();
                }
                break;            
            case 'f':
                if(document.activeElement !== inputElement.current && notStart === false){
                    event.preventDefault();
                    inputElement.current.focus();                    
                }
                break;
            case 'n':
                if(
                    document.activeElement !== inputElement.current &&
                    playerState &&
                    playerState.gameEnd === true
                ){
                    nextGameHandler();
                }
                break;
            default:
                break;
        }            
    }       
    useHotKey(['Escape','f','n'], handleGamePageHotKeys)
    const [ gameText, setGameText ] = useState(gameData.text);
    const [ showButtons, setShowButtons ] = useState(true);
    const dispatch = useDispatch();
    
    // Always derive words client-side via parseQuote - text is always a string
    // Use fallback empty string if gameData.text is not ready yet
    const txtArray = gameData.text && typeof gameData.text === 'string' 
        ? parseQuote(gameData.text) 
        : [];
    let txt_chars = txtArray.map((word) => word.split(''));    
    let chars = []    
    for(let word in txt_chars){
        for(let char in txt_chars[word]){
            chars.push(txt_chars[word][char])
        }
    }
    const generateCharArray = useCallback(() => { // UI purposes 
        let arr = Array.from({ length:chars.length }, ()=>0)
        arr[0] = 1000;
        return arr;
    }, [chars.length]);
    
    const [ charArray, setCharArray ] = useState(generateCharArray);

    const progressCurrCharIdx = () => {
        // Guard: Ensure txt_chars and currWordIdx are valid
        if (txt_chars.length > 0 && typeof currWordIdx === "number" && currWordIdx < txt_chars.length) {
            setBaseIdx(base=> base + txt_chars[currWordIdx].length);
        }
    }        
    const resetBaseIdx = () =>{
        setBaseIdx(base=>base * 0);
    }    
    const clearGameInput = () => {
        if (inputElement.current) {
            inputElement.current.value = "";
        }
    }
    const exitGameHandler = () => {
        // Prevent double-clicks and multiple leave attempts
        if (isLeavingRoom) {
            return;
        }
        
        // Issue #1: Explicitly clear all timers before state reset
        // This prevents timers from firing after component unmounts or state is cleared
        if (practice_game_timer.current !== null) {
            clearInterval(practice_game_timer.current);
            practice_game_timer.current = null;
        }
        if (practice_countdown.current !== null) {
            clearInterval(practice_countdown.current);
            practice_countdown.current = null;
        }
        
        // Optimistically clear local room state
        dispatch(setIsLeavingRoom({isLeaving: true}));
        dispatch(resetGameState());
        dispatch(toggleGame());
        
        // Emit leave room (only once)
        socket.emit('leave room');
        
        // Issue #3: Increased delay and better race condition handling
        // Reset leaving state after a longer delay to ensure socket processes leave
        // and prevent rapid re-joins from causing state conflicts
        setTimeout(() => {
            dispatch(setIsLeavingRoom({isLeaving: false}));
        }, 1500);
    }
    const nextGameHandler = async() => {        
        dispatch(toggleLoader());        
        // Guard: Ensure player exists before accessing properties
        if(!playerState || !playerState.gameEnd){
            dispatch(toggleLoader());
            return;
        }
        if(playerState.gameEnd){
            setShowButtons(false);            
            if(room.gameType === "PRACTICE"){                
                // added async keywords to wait for this function to run before
                // any further instructions run
                try {
                    await loadPracticeGame();
                    dispatch(toggleLoader());
                    // Countdown already started in loadPracticeGame() for immediate start
                }catch(err){
                    throw Error('Something went wrong fetching new game');
                }
                
            }
            else if(room.gameType === "MULTIPLAYER"){                 
                // Use soft reset that preserves room.gameType during transition
                // This prevents the loader from showing unnecessarily
                dispatch(resetGameStateForTransition());
                dispatch(setIsLeavingRoom({isLeaving: true}));
                
                socket.emit('leave room');
                
                // Small delay before joining to ensure server processes leave
                setTimeout(() => {
                    socket.emit('join room');
                    dispatch(setIsLeavingRoom({isLeaving: false}));
                }, 100);
            }
        }
        return;
    }
    const calculatePercentage = (ID) =>{        
        const player = room.players[ID];
        if (!player || typeof player.currentWord !== "number" || txt_chars.length === 0){
            return 0;
        }
        return (player.currentWord/txt_chars.length) * 100;
    }       
    
    const playerList = Object.keys(room.players).map(playerId => {
        const mark = playerId === multiplayer_id || playerId === solo_id;    
        const mark_player_id = room.gameType === "PRACTICE" ? solo_id : multiplayer_id;
        // Guard: Ensure player objects exist before accessing properties
        const player = room.players[playerId];
        const markPlayer = room.players[mark_player_id];
        if (!player) return null;
        
        return (        
          <div key={playerId}>                                    
            <PlayerModel 
            player_name={playerId} 
            marker={mark}                         
            wordsLength={txt_chars.length}
            percentage={mark ? calculatePercentage(mark_player_id):calculatePercentage(playerId)}
            wpm = {mark ? (markPlayer?.wpm ?? 0) : (player?.wpm ?? 0)}
            position = {player?.position ?? -1}
            />
          </div>
        );
    }).filter(Boolean);    
    const loadPracticeGame = async() =>{        
        // Clear any running timers from previous game FIRST to prevent race conditions
        // This prevents old timers from calling endAllPlayerGames() after new game starts
        if (practice_game_timer.current !== null) {
            clearInterval(practice_game_timer.current);
            practice_game_timer.current = null;
        }
        if (practice_countdown.current !== null) {
            clearInterval(practice_countdown.current);
            practice_countdown.current = null;
        }
        
        dispatch(setRoomType({gameType: "PRACTICE"}));    
        dispatch(resetRaceStats());
        // Reset game status to clear any gameEnd flags from previous game
        dispatch(resetGameStatus());
        // Clear practice timer state from previous game to prevent stale timestamps
        // This ensures each new game starts with fresh timer state
        dispatch(clearPracticeGameStartTime());
        dispatch(clearPracticeCountdownStartTime());
        try{
            const { playerObj, gameData } = await loadGameAssets(solo_id,room.gameType);
            // Ensure player object has gameEnd: false for new game
            const freshPlayerObj = {
                ...playerObj,
                gameEnd: false
            };
            dispatch(setRoomPlayers({multiplayer: false, playerObj:freshPlayerObj}));
            dispatch(setGameData({gameData:gameData}));                          
            resetBaseIdx();
            // Start countdown immediately for solo practice games
            dispatch(setCountDown({second:3}));
            dispatch(setPracticeCountdownStartTime({timestamp: Date.now()}));
            dispatch(setCountDownState({newState:true}));
            dispatch(setNotStart({newState:true}));
        }catch(err){
            throw Error(err);
        }
    }    
    let practice_countdown = useRef(null);     
    const startPracticeCountdown = useCallback(() =>  {        
        // Use Redux countdown state instead of local state
        if (roomType !== "PRACTICE" || countdown < 0 || !practiceCountdownStartTime) {
            return;
        }
        // Avoid multiple intervals
        if (practice_countdown.current !== null) {
            return;
        }
        const countdownDuration = 3; // 3 seconds countdown
        practice_countdown.current = setInterval(()=> {
            // Calculate remaining time based on elapsed time (works when tab is inactive)
            const now = Date.now();
            const elapsed = Math.floor((now - practiceCountdownStartTime) / 1000);
            const remaining = Math.max(0, countdownDuration - elapsed);
            
            if (remaining <= 0) {
                dispatch(setNotStart({newState:false}));
                dispatch(setCountDownState({newState:false}));
                dispatch(setCountDown({second:-1}));
                dispatch(clearPracticeCountdownStartTime());
                // Start the game timer when countdown ends
                // Always use the CURRENT gameData.estimatedTime to ensure we use the new game's time
                const currentState = store.getState().game;
                const currentEstimatedTime = currentState.gameData?.estimatedTime ?? 0;
                if (currentEstimatedTime > 0) {
                    // Always reset to use the current game's estimatedTime, even if practiceGameStartTime exists
                    // This ensures we don't use stale initialTime from previous games
                    dispatch(setPracticeGameStartTime({
                        timestamp: Date.now(),
                        initialTime: currentEstimatedTime
                    }));
                }
                if (practice_countdown.current !== null) {
                    clearInterval(practice_countdown.current);
                    practice_countdown.current = null;
                }
                return;
            }
            // Update Redux with calculated remaining time
            dispatch(setCountDown({second:remaining}));
        },100); // Check more frequently for smoother updates
    },[countdown,dispatch,roomType,practiceCountdownStartTime])
    useEffect(()=> {
        if (gameData.text !== gameText){      
            setShowButtons(true);      
            setGameText(gameData.text);
            setCharArray(prevCharArray => prevCharArray =generateCharArray());
            // here is where I can set the charArray to the newly generated charArray
        }
        if (gameData.text !== gameText && room.gameType === "MULTIPLAYER"){
            dispatch(toggleLoader());
        }
    },[gameData.text, dispatch, gameText, generateCharArray, room.gameType])
    let practice_game_timer = useRef(null);    
    // Use Date() timestamps to work even when tab is inactive
    useEffect(()=>{        
        const startGameTimer = () => {
            if(roomType === "PRACTICE" && countdownFlag === false && gameData.estimatedTime >= 0){
                // Avoid multiple intervals
                if (practice_game_timer.current !== null) {
                    return;
                }
                // Store start time when game begins (works even when tab is inactive)
                // Always set a new start time to ensure fresh timestamp for each game
                // Check if we need to reset: no start time OR initialTime doesn't match current gameData
                // This ensures we use the NEW game's estimatedTime, not the previous game's
                const currentState = store.getState().game;
                const needsReset = !currentState.practiceGameStartTime || 
                                  currentState.practiceGameStartTime === null ||
                                  currentState.practiceGameInitialTime !== gameData.estimatedTime;
                
                if (needsReset) {
                    dispatch(setPracticeGameStartTime({
                        timestamp: Date.now(),
                        initialTime: gameData.estimatedTime
                    }));
                }
                
                practice_game_timer.current = setInterval(()=> {
                    const currentState = store.getState().game;
                    const startTime = currentState.practiceGameStartTime;
                    const initialTime = currentState.practiceGameInitialTime;
                    
                    if (!startTime || initialTime === null) {
                        // Fallback: if start time is missing, use decrement pattern
                        const currentEstimatedTime = currentState.gameData.estimatedTime;
                        if (currentEstimatedTime <= 0) {
                            dispatch(endAllPlayerGames());
                            dispatch(clearPracticeGameStartTime());
                            if (practice_game_timer.current !== null) {
                                clearInterval(practice_game_timer.current);
                                practice_game_timer.current = null;
                            }
                            return;
                        }
                        dispatch(updateEstimatedTime());
                        return;
                    }
                    
                    // Calculate remaining time based on elapsed time (works when tab is inactive)
                    const now = Date.now();
                    const elapsed = Math.floor((now - startTime) / 1000);
                    const remaining = Math.max(0, initialTime - elapsed);
                    
                    if (remaining <= 0) {
                        dispatch(endAllPlayerGames());
                        dispatch(clearPracticeGameStartTime());
                        if (practice_game_timer.current !== null) {
                            clearInterval(practice_game_timer.current);
                            practice_game_timer.current = null;
                        }
                        return;
                    }
                    
                    // Update Redux with calculated remaining time
                    // Only update if it's different to avoid unnecessary re-renders
                    if (currentState.gameData.estimatedTime !== remaining) {
                        // Set estimatedTime directly instead of decrementing
                        dispatch(setGameData({gameData: {...currentState.gameData, estimatedTime: remaining}}));
                    }
                },100); // Check more frequently for smoother updates
            }
        }
        startGameTimer();
        return () => {
            if (practice_game_timer.current !== null) {
                clearInterval(practice_game_timer.current);
                practice_game_timer.current = null;
            }
        }
    },[countdownFlag, gameData.estimatedTime,dispatch,roomType,practiceGameStartTime,practiceGameInitialTime])
    useEffect(()=>{        
        startPracticeCountdown();
        return ()=>{
            if (practice_countdown.current !== null) {
                clearInterval(practice_countdown.current);
                practice_countdown.current = null;
            }
        }
    },[countdown,startPracticeCountdown])
    useEffect(()=>{        
        // Helper function to check if we're in post-game state (showing "Next game" button)
        // This is when updates should be blocked to keep state "stale"
        const isPostGameState = () => {
            const currentState = store.getState().game;
            const roomType = currentState.room?.gameType;
            const estimatedTime = currentState.gameData?.estimatedTime ?? -1;
            
            if (roomType === "MULTIPLAYER") {
                // In multiplayer, we're in post-game state when:
                // 1. Timer has ended (estimatedTime <= 0)
                // 2. Current player has finished (gameEnd: true)
                // This allows updates while timer is running, even if some players finished
                const currentPlayerId = currentState.multiplayer_id;
                const currentPlayer = currentState.room?.players?.[currentPlayerId];
                return estimatedTime <= 0 && currentPlayer?.gameEnd === true;
            } else if (roomType === "PRACTICE") {
                // For practice games, check if timer ended and player finished
                const player = currentState.room?.players?.[currentState.solo_id];
                return estimatedTime <= 0 && player?.gameEnd === true;
            }
            return false;
        };
        
        // Helper function to check if socket events should be ignored
        // Allow updates while timer is running, even if some players have finished
        // Only block updates when we're in post-game state (timer ended + player finished)
        const shouldIgnoreSocketEvent = () => {
            const currentState = store.getState().game;
            // Ignore if leaving room or not on game page
            if (currentState.isLeavingRoom || !currentState.gamePage) {
                return true;
            }
            // Only ignore if we're in post-game state (timer ended + current player finished)
            // This allows updates to continue while timer is running, showing other players' progress
            return isPostGameState();
        };
        
        const loadNewPlayer = (data) => {
            // Guard: Don't update state after exit or after game ends
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            dispatch(addPlayerToRoom({player_id: data.new_player_id}));
        }
        const updateAPlayer = (data) => {
            // Guard: Don't update state after exit or after game ends
            // This prevents stale player updates from breaking the post-game screen
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            const {player_id,wpm,progress} = data; // progress is the current word
            dispatch(updatePlayerWPM({player_id:player_id, wpm:wpm}));
            dispatch(updatePlayerWord({player_id: player_id, currWord: progress}));
        }
        const deleteAPlayer = (data) => {
            // Guard: Don't update state after exit or after game ends
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            const {delete_player} = data;            
            dispatch(deletePlayerFromRoom({delete_player:delete_player}));
        }
        const updateCountDown = (data) => {
            // Guard: Don't update state after exit or after game ends
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            dispatch(setCountDown({second:data}))
        }
        const resetCountDown = () => {
            // Guard: Don't update state after exit or after game ends
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            dispatch(setCountDown({second:-1}))
        }
        const startMultiplayerGame = () => {
            // Guard: Don't update state after exit or after game ends
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            dispatch(setRoomStartTime()); // set start time for every player in room
            dispatch(toggleNotStart()); // activate game input component
            dispatch(toggleCountDown()); // activate count down component            
        }
        const handleEstimatedTime = (data) => {
            // Guard: Don't update state after exit or after game ends
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            dispatch(updateEstimatedTime())
        }
        const handleTimerEnd = () => {
            // Guard: Don't process if already leaving or not on game page
            const currentState = store.getState().game;
            if (currentState.isLeavingRoom || !currentState.gamePage) {
                return;
            }
            
            const roomType = currentState.room?.gameType;
            
            // Clear practice timers if this is a practice game
            if (roomType === "PRACTICE") {
                if (practice_game_timer.current !== null) {
                    clearInterval(practice_game_timer.current);
                    practice_game_timer.current = null;
                }
                if (practice_countdown.current !== null) {
                    clearInterval(practice_countdown.current);
                    practice_countdown.current = null;
                }
                dispatch(clearPracticeGameStartTime());
            }
            
            // End the game: disable input and mark all players as finished
            clearGameInput();
            dispatch(endAllPlayerGames()); // Sets gameEnd: true for all players
            dispatch(toggleNotStart()); // Disables input (sets notStart: true)
            
            // DO NOT reset game state or leave room
            // This allows players to:
            // 1. See final results (WPM, positions, stats)
            // 2. View the chart component
            // 3. Press "Next game" to start a new game
            // State will be reset when "Next game" is pressed via nextGameHandler
        }
        const handlePlayerPosition = (data) => {
            // Guard: Don't update state after exit or after game ends
            // Position updates after game end can break the post-game screen
            if (shouldIgnoreSocketEvent()) {
                return;
            }
            const { player_id, win_position } = data;            
            dispatch(updatePlayerPosition({player_id:player_id,win_position:win_position}));
        }
        const loadMultiplayerGame = (data) => {
            // Guard: Don't update state after exit
            // Note: We don't check isGameEnded here because loadMultiplayerGame
            // is specifically for loading a NEW game, which should happen even if previous game ended
            const currentState = store.getState().game;
            if (currentState.isLeavingRoom || !currentState.gamePage) {
                return;
            }
            const {gameData} = data;
            const gameAssets = {
                text: gameData.text,
                totalCharacters: gameData.totalCharacters,
                estimatedTime: gameData.estimatedTime,                
            }
            // Use soft reset to preserve room.gameType during transition
            dispatch(resetGameStateForTransition());
            const playerAssets = gameData.player_ids; // existing players            
            if(playerAssets.length > 0){
                playerAssets.forEach((ID) => {
                    dispatch(addPlayerToRoom({player_id: ID}));
                });                
            }        
            resetBaseIdx();
            dispatch(addPlayerToRoom({player_id:socket.id}));
            dispatch(setRoomType({gameType: "MULTIPLAYER"}));
            dispatch(setGameData({gameData:gameAssets}));     
            dispatch(startCountDown());
        }
        socket.on("player joined room",loadNewPlayer);
        socket.on("player left room", deleteAPlayer);
        socket.on("update a player",updateAPlayer);
        socket.on("countdown",updateCountDown);
        socket.on("reset countdown", resetCountDown);
        socket.on("game started", startMultiplayerGame);
        socket.on("game timer update", handleEstimatedTime);
        socket.on("game timer end", handleTimerEnd);
        socket.on("update player position", handlePlayerPosition);
        socket.on("game room data", loadMultiplayerGame)
        return () => {
            socket.off("player joined room", loadNewPlayer);
            socket.off("player left room", deleteAPlayer);
            socket.off("update a player", updateAPlayer);
            socket.off("countdown", updateCountDown);
            socket.off("reset countdown",resetCountDown);
            socket.off("game started", startMultiplayerGame);
            socket.off("game timer update", handleEstimatedTime);
            socket.off("game timer end", handleTimerEnd);
            socket.off("update player position", handlePlayerPosition);
            socket.off("game room data", loadMultiplayerGame);            
        }        
    });
    
    // Issue #1: Cleanup effect - runs when component unmounts
    // This ensures timers are always cleared even if exitGameHandler wasn't called
    // Since GamePage is conditionally rendered based on gamePage state, when gamePage
    // becomes false, this component will unmount and this cleanup will run
    useEffect(() => {
        return () => {
            // Cleanup all timers on unmount
            if (practice_game_timer.current !== null) {
                clearInterval(practice_game_timer.current);
                practice_game_timer.current = null;
            }
            if (practice_countdown.current !== null) {
                clearInterval(practice_countdown.current);
                practice_countdown.current = null;
            }
        };
    }, []); // Empty deps - only runs on mount/unmount
    const chartComponent = useMemo(() => {
        return gameEnd ? <ChartComponent timeStampData={race_stats} /> : null;
    }, [gameEnd, race_stats]);

    // Show a clear "preparing" / loading state until we have a valid player in the room
    // Also guard against missing gameData.text
    const isGameReady =
        roomType !== "NONE" &&
        !!player_id &&
        !!playerState &&
        !!gameData.text &&
        typeof gameData.text === 'string';

    if (!isGameReady) {
        return (
            <section className="game-page">
                <Loader />
            </section>
        );
    }

    return (
        <section className='game-page'>              
            <CountDown
                data={-1}
            />
            <header
            style={{textAlign:"center", fontWeight: "bold"}} className="section--three-grid">
                <div></div>
                <div></div>
                <GameTimer/>
            </header>
            <section className = "section--three-grid">
            <div></div>
            <div></div>
            <h4 
            style = {{color: "white"}}
            className = "game-page__title"
            >WPM</h4>
            </section>            
            <section className = 'game-page__players'> 
                {playerList}                              
            </section>
            <section className = 'game-page__content'>                
                <p>
                {chars.map((word,index) => (
                    <span
                    className = {charArray[index] === 0 ? 'gray' : charArray[index] === 1 ? 'game-page__char--green' : charArray[index] === -1 ? 'game-page__char--red': 'highlightCurrent'}
                    style = {{color: "white"}}
                    key={index}>{word}
                    </span>
                ))}
                </p>
            </section>
            <section className = 'game-page__input'>
                <GameInput                
                words = {txt_chars}
                baseIdx = {baseCurrIdx}                
                progressCurrCharIdx = {progressCurrCharIdx}     
                chars = {chars}
                charArray = {charArray}
                setCharArray = {setCharArray}
                ref = {inputElement}
                ></GameInput>
            </section>
            <section className = 'game-page__options'>
                <div className="game-input__hotkeys">
                    <small>Press <span className="button-hotkey">escape</span> to Disengage</small>
                    <small>Press <span className="button-hotkey">f</span> to Engage</small>
                </div>
                <div className="game-page__buttons">
                    {
                        showButtons &&
                        (<Button 
                        classes="neon-button neon-button--red" 
                        text="Exit game"
                        action={exitGameHandler}
                        hotkey="escape"
                        activeEl={inputElement.current}
                        />)                
                    }                
                    {gameEnd === true ?
                        showButtons &&
                        (<Button 
                        classes="neon-button" 
                        text="Next game"
                        action={nextGameHandler}
                        hotkey="n"
                        activeEl={inputElement.current}
                        />)                    
                    : null}
                </div>
            </section>
            {chartComponent}
        </section>
    );
}
